import * as React from 'react';

import { useQuery } from '@apollo/client';

import PersonalizationSettingsAttributesQuery from 'bundles/personalization-settings/queries/PersonalizationSettingsAttributes.graphql';
import type {
  PersonalizationSettingsAttributesQuery as PersonalizationSettingsAttributesQueryType,
  PersonalizationSettingsAttributesQueryVariables,
} from 'bundles/personalization-settings/queries/__generated__/PersonalizationSettingsAttributes';
import {
  type PersonalizationFieldValues,
  formatLearnerObjectAttributes,
} from 'bundles/personalization-settings/utils/formatLearnerObjectAttributes';

export type UsePersonalizationSettingsAttributesResult = {
  values: PersonalizationFieldValues;
  loading: boolean;
  error: ReturnType<typeof useQuery>['error'];
};

/**
 * Reads the logged-in learner's explicit Learner Object attributes in a single
 * query on mount and maps them to per-field display values. The Personalization
 * tab is auth-gated upstream (mount site + Statsig gate), so no client-side
 * logged-in guard is needed here. `errorPolicy: 'all'` lets partial data render
 * while a field error falls back to its placeholder.
 */
const usePersonalizationSettingsAttributes = (): UsePersonalizationSettingsAttributesResult => {
  const { data, loading, error } = useQuery<
    PersonalizationSettingsAttributesQueryType,
    PersonalizationSettingsAttributesQueryVariables
  >(PersonalizationSettingsAttributesQuery, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });

  return {
    // `formatLearnerObjectAttributes` returns fresh arrays/objects each call --
    // memoize on `data` so `values.desiredRole`/`.skills`/`.currentRole` keep a
    // stable reference across renders where the query result hasn't changed,
    // otherwise every downstream seeding memo keyed on those fields recomputes.
    values: React.useMemo(() => formatLearnerObjectAttributes(data), [data]),
    loading,
    error,
  };
};

export default usePersonalizationSettingsAttributes;
